import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QrCode, Loader2, AlertCircle, ExternalLink } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicScan() {
  const { code } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let redirected = false;
    axios
      .get(`${API_BASE}/public/qr/${code}`)
      .then((res) => {
        const data = res.data;
        if (!data.found) {
          setState({ status: "notfound" });
          return;
        }
        if (data.has_destination) {
          setState({ status: "redirecting", url: data.destination_url });
          setTimeout(() => {
            if (!redirected) {
              redirected = true;
              window.location.replace(data.destination_url);
            }
          }, 900);
        } else {
          setState({ status: "inactive" });
        }
      })
      .catch(() => setState({ status: "error" }));
    return () => {
      redirected = true;
    };
  }, [code]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0B0F17] text-white p-6 subtle-grid"
      data-testid="public-scan-page"
    >
      <div className="max-w-md w-full text-center fade-in">
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-sky-500 flex items-center justify-center shadow-lg shadow-sky-500/40">
            <QrCode className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="jscan-brand text-3xl">J-scan</div>
            <div className="text-[10px] uppercase tracking-widest text-sky-300/80">
              Kode {code}
            </div>
          </div>
        </div>

        {state.status === "loading" && (
          <div className="space-y-3">
            <Loader2 className="w-6 h-6 animate-spin text-sky-400 mx-auto" />
            <p className="text-slate-400 text-sm">Menyiapkan halaman review...</p>
          </div>
        )}

        {state.status === "redirecting" && (
          <div className="space-y-4" data-testid="scan-redirecting">
            <Loader2 className="w-6 h-6 animate-spin text-sky-400 mx-auto" />
            <p className="text-slate-200 text-base">
              Mengarahkan kamu ke halaman review Google Maps...
            </p>
            <a
              href={state.url}
              className="inline-flex items-center gap-1.5 text-sky-400 text-sm hover:underline jscan-mono"
              data-testid="scan-manual-link"
            >
              Klik di sini jika tidak dialihkan <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}

        {state.status === "inactive" && (
          <div
            className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 space-y-3"
            data-testid="scan-inactive"
          >
            <AlertCircle className="w-8 h-8 text-amber-400 mx-auto" />
            <div className="jscan-brand text-xl">QR Belum Aktif</div>
            <p className="text-sm text-amber-100/80 leading-relaxed">
              Akrilik ini sudah terdaftar, tapi tujuan review belum diatur. Silakan hubungi
              penjual akrilik untuk mengaktifkan.
            </p>
          </div>
        )}

        {state.status === "notfound" && (
          <div
            className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-6 space-y-3"
            data-testid="scan-notfound"
          >
            <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
            <div className="jscan-brand text-xl">QR Tidak Ditemukan</div>
            <p className="text-sm text-rose-100/80">
              Kode ini tidak terdaftar di sistem J-scan.
            </p>
          </div>
        )}

        {state.status === "error" && (
          <div className="text-slate-400 text-sm">Terjadi kesalahan. Coba lagi nanti.</div>
        )}

        <div className="mt-10 text-[10px] uppercase tracking-widest text-slate-600 jscan-mono">
          Powered by J-scan
        </div>
      </div>
    </div>
  );
}
